import { createFileRoute } from '@tanstack/react-router';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  ScrollArea,
  SimpleGrid,
  Table,
  Text,
  TextInput,
  Tooltip, rem,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconActivity,
  IconBuildingSkyscraper,
  IconCalendar,
  IconDownload,
  IconRefresh,
  IconSearch,
  IconShieldHalf,
  IconUserShield,
  IconX,
} from '@tabler/icons-react';
import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchAllPages } from '@/shared/api/jsonapi/client';
import { parseBeIso } from '@/features/attendance/api';
import { EmptyState } from '@/shared/components/EmptyState';
import { StatCard } from '@/shared/components/StatCard';
import { mw, MW_CARD_SHADOW, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileAuditLog } from '@/features/admin/mobile/MobileAuditLog';
import { notifications } from '@mantine/notifications';
import { downloadCsv, type CsvColumn } from '@/shared/lib/exportCsv';
import type { ImpersonationLog } from '@/shared/types/entities';
import { IMPERSONATION_END_REASON } from '@/features/auth/impersonation';

export const Route = createFileRoute('/_app/admin/audit-log')({ component: AuditLogRoute });

/** Selettore: audit log mobile (MaeDesign) vs tabella desktop. */
function AuditLogRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileAuditLog /> : <AuditLogPage />;
}

const PAGE_SIZE = 25;

const CSV_COLUMNS: Array<CsvColumn<ImpersonationLog>> = [
  { header: 'Inizio', accessor: (r) => r.startedAt },
  { header: 'Fine', accessor: (r) => r.endedAt },
  { header: 'Motivo fine', accessor: (r) => (r.endReason ? IMPERSONATION_END_REASON[r.endReason] ?? r.endReason : '') },
  { header: 'Accesso portale', accessor: (r) => r.portalAccountId ?? '' },
  { header: 'Attore', accessor: (r) => r.actorUsername ?? r.actorUser?.id },
  { header: 'Target', accessor: (r) => r.targetUsername ?? r.targetUser?.id },
  { header: 'Azienda', accessor: (r) => r.targetTenant?.name ?? r.targetTenant?.id },
  { header: 'IP', accessor: (r) => r.ip },
];

function AuditLogPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 200);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [page, setPage] = useState(1);

  const listQ = useQuery({
    queryKey: ['impersonation-logs-all'],
    queryFn: () => fetchAllPages<ImpersonationLog>('impersonation-logs', {
      sort: ['-startedAt']
    }, 500),
  });

  const all = useMemo(() => listQ.data ?? [], [listQ.data]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const [from, to] = range;
    return all.filter((row) => {
      if (q) {
        const hay = `${row.actorUsername ?? ''} ${row.targetUsername ?? ''} ${row.targetTenant?.name ?? ''} ${row.ip ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (from && row.startedAt && parseBeIso(row.startedAt) < dayjs(from).startOf('day').toDate()) return false;
      if (to && row.startedAt && parseBeIso(row.startedAt) > dayjs(to).endOf('day').toDate()) return false;
      return true;
    });
  }, [all, debouncedSearch, range]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageData = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeCount = useMemo(() => all.filter((r) => !r.endedAt).length, [all]);
  const distinctActors = useMemo(
    () => new Set(all.map((r) => r.actorUsername ?? r.actorUser?.id).filter(Boolean)).size,
    [all],
  );
  const distinctTenants = useMemo(
    () => new Set(all.map((r) => r.targetTenant?.name ?? r.targetTenant?.id).filter(Boolean)).size,
    [all],
  );

  // Esporta le righe correnti caricate (già filtrate client-side).
  const exportCsv = () => {
    const rows = filtered;
    if (rows.length === 0) {
      notifications.show({ color: 'orange', message: 'Nessun dato da esportare' });
      return;
    }
    downloadCsv(`audit-log-${dayjs().format('YYYYMMDD-HHmm')}`, CSV_COLUMNS, rows);
    notifications.show({ color: 'green', message: `${rows.length} righe esportate` });
  };

  const card: CSSProperties = {
    border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
  };
  const thStyle: CSSProperties = {
    background: mw.surface2, color: mw.ink300, fontSize: 'var(--fs-compact)', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${mw.line}`,
    whiteSpace: 'nowrap',
  };
  const initials = (s?: string | null) => {
    const parts = (s ?? '').trim().split(/[\s@._-]+/).filter(Boolean);
    return (parts.map((w) => w[0]).slice(0, 2).join('') || '?').toUpperCase();
  };
  const chip = (label: string, bg: string, color: string, code = false) => (
    <span style={{ display: 'inline-block', fontSize: code ? 'var(--fs-compact)' : 'var(--fs-compact)', fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: bg, color, ...(code ? MW_CODE_STYLE : null), whiteSpace: 'nowrap' }}>{label}</span>
  );

  return (
    <MaeShell>
      <MaePageHead
        title="Audit log"
        sub="Storico delle sessioni di impersonificazione SuperAdmin — sola lettura."
        icon={<IconShieldHalf size={23} />}
        actions={
          <>
            <Tooltip label="Aggiorna">
              <ActionIcon
                variant="light"
                color="dark"
                size="lg"
                aria-label="Aggiorna lista impersonificazioni"
                onClick={() => qc.invalidateQueries({ queryKey: ['impersonation-logs-all'] })}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Button
              variant="light"
              color="dark"
              leftSection={<IconDownload size={16} />}
              onClick={exportCsv}
              disabled={filtered.length === 0}
            >
              Esporta CSV
            </Button>
          </>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={14} mb={18}>
        <StatCard label="Sessioni totali" value={all.length} icon={IconShieldHalf} color="brand" loading={listQ.isLoading} />
        <StatCard label="Attive ora" value={activeCount} icon={IconActivity} color={activeCount > 0 ? 'orange' : 'gray'} loading={listQ.isLoading} />
        <StatCard label="Attori distinti" value={distinctActors} icon={IconUserShield} color="blue" loading={listQ.isLoading} />
        <StatCard label="Aziende coinvolte" value={distinctTenants} icon={IconBuildingSkyscraper} color="gray" loading={listQ.isLoading} />
      </SimpleGrid>

      <div style={{ ...card, padding: '14px 16px', marginBottom: 16 }}>
        <Group wrap="wrap" gap="sm">
          <TextInput
            placeholder="Cerca attore, target, azienda o IP…"
            leftSection={<IconSearch size={14} />}
            rightSection={search ? (
              <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={300}
          />
          <DatePickerInput
            type="range"
            placeholder="Periodo"
            value={range}
            onChange={(v) => setRange(v as [Date | null, Date | null])}
            leftSection={<IconCalendar size={14} />}
            clearable
            w={260}
            valueFormat="DD/MM/YYYY"
          />
          {(search || range[0] || range[1]) && (
            <Button
              variant="subtle"
              color="dark"
              size="xs"
              onClick={() => { setSearch(''); setRange([null, null]); }}
            >
              Pulisci filtri
            </Button>
          )}
          <div style={{ marginLeft: 'auto' }}>
            {filtered.length !== all.length
              ? chip(`${filtered.length} di ${all.length}`, mw.accentTint, mw.accentText)
              : chip(`${all.length} sessioni`, mw.surface2, mw.ink500)}
          </div>
        </Group>
      </div>

      {listQ.isError ? <QueryErrorAlert error={listQ.error} onRetry={() => { void listQ.refetch(); }} /> : listQ.isLoading ? (
        <Center py={60}><Loader /></Center>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, overflow: 'hidden' }}>
          <EmptyState
            icon={IconShieldHalf}
            title={all.length === 0 ? 'Nessuna sessione di impersonificazione registrata' : 'Nessun risultato per i filtri correnti'}
            description={all.length === 0
              ? 'Le sessioni di impersonificazione SuperAdmin compariranno qui appena avviate.'
              : 'Prova ad allargare il periodo o pulire la ricerca.'}
          />
        </div>
      ) : (
        <>
          <div style={{ ...card, overflow: 'hidden' }}>
            <ScrollArea type="auto">
              <Table verticalSpacing={12} horizontalSpacing="md" highlightOnHover stickyHeader
                styles={{ table: { fontFamily: 'inherit' } }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={thStyle}>Inizio</Table.Th>
                    <Table.Th style={thStyle}>Fine</Table.Th>
                    <Table.Th style={thStyle}>Durata</Table.Th>
                    <Table.Th style={thStyle}>Attore</Table.Th>
                    <Table.Th style={thStyle}>Target</Table.Th>
                    <Table.Th style={thStyle}>Azienda</Table.Th>
                    <Table.Th style={thStyle}>IP</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageData.map((row) => {
                    const start = row.startedAt ? parseBeIso(row.startedAt) : null;
                    const end = row.endedAt ? parseBeIso(row.endedAt) : null;
                    const durMin = start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : null;
                    const actor = row.actorUsername ?? row.actorUser?.id ?? '—';
                    const target = row.targetUsername ?? row.targetUser?.id ?? '—';
                    const tenantName = row.targetTenant?.name ?? row.targetTenant?.id;
                    return (
                      <Table.Tr key={row.id}>
                        <Table.Td>
                          {start ? (
                            <>
                              <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }}>{dayjs(start).format('DD/MM/YYYY')}</Text>
                              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{dayjs(start).format('HH:mm')}</Text>
                            </>
                          ) : <Text style={{ color: mw.ink300 }}>—</Text>}
                        </Table.Td>
                        <Table.Td>
                          {end ? (
                            <>
                              <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }}>{dayjs(end).format('DD/MM/YYYY')}</Text>
                              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>
                                {dayjs(end).format('HH:mm')}
                                {row.endReason ? ` · ${IMPERSONATION_END_REASON[row.endReason] ?? row.endReason}` : ''}
                              </Text>
                            </>
                          ) : chip('● Attiva', mw.warningBg, mw.warning)}
                        </Table.Td>
                        <Table.Td>
                          {durMin != null
                            ? chip(durMin < 60 ? `${durMin} min` : `${(durMin / 60).toFixed(1)} h`, mw.surface2, mw.ink500)
                            : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>in corso</Text>}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={9} wrap="nowrap">
                            <span style={{ width: rem(30), height: rem(30), borderRadius: 9, flexShrink: 0, background: mw.brandFill, color: 'var(--on-brand)', fontSize: 'var(--fs-compact)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials(actor)}</span>
                            <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }} lineClamp={1}>{actor}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={9} wrap="nowrap">
                            <span style={{ width: rem(30), height: rem(30), borderRadius: 9, flexShrink: 0, background: mw.accentTint, color: mw.accentText, fontSize: 'var(--fs-compact)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials(target)}</span>
                            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink900 }} lineClamp={1}>{target}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          {tenantName ? chip(tenantName, mw.accentTint, mw.accentText) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}
                        </Table.Td>
                        <Table.Td>
                          {row.ip ? chip(row.ip, mw.surface2, mw.ink500, true) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </div>
          {totalPages > 1 && (
            <Group justify="center" mt={16}>
              <Pagination
                total={totalPages}
                value={safePage}
                onChange={setPage}
                size="sm"
                color={mw.brand}
              />
            </Group>
          )}
        </>
      )}
    </MaeShell>
  );
}
